/**
 * GovCostAnalysisSidebar — cost engine parameters + strategy + sensitivity.
 * Right rail on the cost analysis tab.
 */
import React, { useState } from 'react';
import api from '../../services/api';
import { S, GOV_COLORS, fmtMoney, fmtMoneyFull } from './govShared';

interface Props {
  version: any;
  readOnly: boolean;
  onChanged: () => void;
}

const SENSITIVITY_LABELS: Record<string, string> = {
  contingency_5: 'Contingency +5%',
  contingency_5_neg: 'Contingency -5%',
  op_3: 'O&P +3%',
  op_3_neg: 'O&P -3%',
  bond_2: 'Bond +2%',
  labor_15pct: 'Labor +15%',
  labor_15pct_neg: 'Labor -15%',
  material_10pct: 'Material +10%',
};

function labelFor(key: string): string {
  const map: Record<string, string> = {
    'contingency_+5': 'Contingency +5%',
    'contingency_-5': 'Contingency -5%',
    'op_+3': 'O&P +3%',
    'op_-3': 'O&P -3%',
    'bond_+2': 'Bond +2%',
    'labor_+15pct': 'Labor +15%',
    'labor_-15pct': 'Labor -15%',
    'material_+10pct': 'Material +10%',
  };
  return map[key] || key;
}

export default function GovCostAnalysisSidebar({ version, readOnly, onChanged }: Props) {
  const [saving, setSaving] = useState(false);

  const patchField = async (patch: any) => {
    if (readOnly) return;
    setSaving(true);
    try {
      await api.patch(`/gov/cost-analysis-versions/${version.id}`, patch);
      onChanged();
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const NumInput = ({ label, field, suffix, step = 0.5 }: { label: string; field: string; suffix?: string; step?: number }) => {
    const [local, setLocal] = useState<string>(String(version[field] ?? ''));
    return (
      <div style={{ marginBottom: 10 }}>
        <div style={{ ...S.kvLabel, marginBottom: 4 }}>{label}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            type="number"
            step={step}
            value={local}
            disabled={readOnly}
            onChange={e => setLocal(e.target.value)}
            onBlur={() => {
              const num = Number(local);
              if (isFinite(num) && num !== Number(version[field])) patchField({ [field]: num });
            }}
            style={{ ...S.input, flex: 1, opacity: readOnly ? 0.6 : 1 }}
          />
          {suffix && <span style={{ color: GOV_COLORS.textMuted, fontSize: 12, minWidth: 20 }}>{suffix}</span>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Cost engine params */}
      <div style={S.card}>
        <h3 style={S.panelTitle}>⚙️ Cost Engine</h3>
        <NumInput label="Contingency" field="contingency_pct" suffix="%" />
        <NumInput label="Overhead & Profit" field="op_pct" suffix="%" />
        <NumInput label="Bond" field="bond_pct" suffix="%" step={0.25} />
        <NumInput label="Risk Buffer (flat $)" field="risk_buffer_fixed" step={500} />
        <NumInput label="Strategic Adj (flat $, can be negative)" field="strategic_adjustment_fixed" step={500} />
        <NumInput label="Round Bid Up To Nearest" field="rounding_increment" step={100} />
        <div style={{ color: GOV_COLORS.textDim, fontSize: 10, marginTop: 8 }}>
          Formula v{version.calculation_version || '1.0'}: (direct + contingency) × (1 + O&P/100) × (1 + bond/100) + risk buffer + strategic adj
        </div>
      </div>

      {/* Bid strategy */}
      <div style={S.card}>
        <h3 style={S.panelTitle}>🎯 Bid Strategy</h3>
        <div style={{ marginBottom: 10 }}>
          <div style={{ ...S.kvLabel, marginBottom: 4 }}>Approach</div>
          <select value={version.bid_strategy || 'balanced'} disabled={readOnly}
            onChange={e => patchField({ bid_strategy: e.target.value })} style={{ ...S.select, width: '100%' }}>
            <option value="aggressive">🔥 Aggressive (chase award)</option>
            <option value="balanced">⚖️ Balanced</option>
            <option value="walk_away_high">🚶 Walk-Away High (only if we want it)</option>
          </select>
        </div>
        <NumInput label="Win Probability (est)" field="win_probability_pct" suffix="%" step={5} />
        <div style={{ marginBottom: 4 }}>
          <div style={{ ...S.kvLabel, marginBottom: 4 }}>Competitor Notes</div>
          <textarea
            defaultValue={version.competitor_notes || ''}
            disabled={readOnly}
            onBlur={e => {
              if (e.target.value !== (version.competitor_notes || '')) patchField({ competitor_notes: e.target.value });
            }}
            style={{ ...S.input, width: '100%', minHeight: 60, resize: 'vertical' as const, opacity: readOnly ? 0.6 : 1 }}
            placeholder="Who else is likely bidding? What do we know about their pricing?"
          />
        </div>
      </div>

      {/* Sensitivity */}
      {version.sensitivity_json?.scenarios && (
        <div style={S.card}>
          <h3 style={S.panelTitle}>📊 Sensitivity</h3>
          <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, marginBottom: 8 }}>Base: {fmtMoney(version.sensitivity_json.base)}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {Object.entries(version.sensitivity_json.scenarios).map(([key, s]: any) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12, borderBottom: `1px solid ${GOV_COLORS.border}` }}>
                <span style={{ color: GOV_COLORS.textMuted }}>{labelFor(key)}</span>
                <span style={{ color: s.delta_from_base > 0 ? GOV_COLORS.danger : GOV_COLORS.success, fontWeight: 700 }}>
                  {s.delta_from_base > 0 ? '+' : ''}{fmtMoney(s.delta_from_base)} ({s.delta_pct > 0 ? '+' : ''}{s.delta_pct}%)
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {saving && <div style={{ color: GOV_COLORS.textMuted, fontSize: 11, textAlign: 'center' }}>Saving…</div>}
    </div>
  );
}
